import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { writeFetch, getSession } from '../../api';
import AISuggestionsPanel from './AISuggestionsPanel';
import {
  notifyFamilyMemberAdded,
  notifyLifeEventUpdated,
  notifyMemorialTreeLinked,
  useFamilyNetworkNotifications,
} from '../../hooks/useFamilyNetworkNotifications';

const {
  FiUsers, FiPlus, FiEdit2, FiTrash2, FiX, FiCheck,
  FiUser, FiHeart, FiLink, FiAlertTriangle, FiRefreshCw,
  FiSearch, FiChevronDown, FiCalendar, FiInfo, FiBell, FiZap,
} = FiIcons;

const TABLE = 'user_family_members_1749000000';

const RELATIONSHIPS = [
  'Spouse / Partner', 'Parent', 'Child', 'Sibling',
  'Grandparent', 'Grandchild', 'Aunt / Uncle', 'Niece / Nephew',
  'Cousin', 'In-Law', 'Step-Parent', 'Step-Child',
  'Half-Sibling', 'Guardian', 'Friend (close)', 'Other',
];

const RELATION_COLORS = {
  'Spouse / Partner': 'bg-amber-100 text-amber-700 border-amber-200',
  'Parent':           'bg-blue-100 text-blue-700 border-blue-200',
  'Child':            'bg-green-100 text-green-700 border-green-200',
  'Sibling':          'bg-purple-100 text-purple-700 border-purple-200',
  'Grandparent':      'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Grandchild':       'bg-teal-100 text-teal-700 border-teal-200',
};

const EMPTY_FORM = {
  full_name: '', relationship: '', birth_year: '', is_deceased: false,
  death_year: '', bio: '', avatar_url: '', linked_memorial_id: '', linked_memorial_name: '',
};

// ── Tabs ──────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'members',     label: 'My Network',       icon: FiUsers },
  { id: 'suggestions', label: 'AI Suggestions',   icon: FiZap   },
];

// ── Add / Edit Modal ─────────────────────────────────────────────────────────
function MemberModal({ member, onClose, onSave, saving }) {
  const [form, setForm] = useState(member ? {
    full_name: member.full_name || '',
    relationship: member.relationship || '',
    birth_year: member.birth_year || '',
    is_deceased: !!member.is_deceased,
    death_year: member.death_year || '',
    bio: member.bio || '',
    avatar_url: member.avatar_url || '',
    linked_memorial_id: member.linked_memorial_id || '',
    linked_memorial_name: member.linked_memorial_name || '',
  } : { ...EMPTY_FORM });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.relationship) return;
    onSave(form);
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        className="bg-white rounded-[32px] w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
        initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
      >
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-serif text-accent-navy">
              {member ? 'Edit Family Member' : 'Add Family Member'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-xl transition-colors">
              <SafeIcon icon={FiX} className="w-5 h-5 text-text-muted" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-1.5">Full Name *</label>
              <input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Enter full name" required
                className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-1.5">Relationship *</label>
              <div className="relative">
                <select value={form.relationship} onChange={e => set('relationship', e.target.value)} required
                  className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light appearance-none">
                  <option value="">Select relationship…</option>
                  {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-1.5">Birth Year</label>
                <input type="number" min="1800" max={new Date().getFullYear()} value={form.birth_year}
                  onChange={e => set('birth_year', e.target.value)} placeholder="e.g. 1960"
                  className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light" />
              </div>
              <div className="flex flex-col justify-end pb-1">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div onClick={() => set('is_deceased', !form.is_deceased)}
                    className={`w-12 h-6 rounded-full transition-colors flex items-center px-1 ${form.is_deceased ? 'bg-accent-navy' : 'bg-gray-200'}`}>
                    <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_deceased ? 'translate-x-6' : 'translate-x-0'}`} />
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-widest text-text-muted">Deceased</span>
                </label>
              </div>
            </div>
            {form.is_deceased && (
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-1.5">Year of Passing</label>
                <input type="number" min="1800" max={new Date().getFullYear()} value={form.death_year}
                  onChange={e => set('death_year', e.target.value)} placeholder="e.g. 2023"
                  className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light" />
              </div>
            )}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-1.5">Short Bio</label>
              <textarea value={form.bio} onChange={e => set('bio', e.target.value)} placeholder="A brief description…" rows={2}
                className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light resize-none" />
            </div>
            <div className="p-4 bg-accent-navy/5 rounded-2xl border border-accent-navy/10">
              <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3 flex items-center gap-2">
                <SafeIcon icon={FiLink} className="w-3.5 h-3.5" /> Link to a Memorial (optional)
              </p>
              <input value={form.linked_memorial_name} onChange={e => set('linked_memorial_name', e.target.value)}
                placeholder="Memorial name (e.g. Eleanor Thompson)"
                className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-white mb-2" />
              <input value={form.linked_memorial_id} onChange={e => set('linked_memorial_id', e.target.value)}
                placeholder="Memorial ID (e.g. m1)"
                className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-white" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose}
                className="flex-1 py-3 border-2 border-brand-dark rounded-2xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-navy transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="flex-1 py-3 bg-accent-gold text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" /> : <SafeIcon icon={FiCheck} className="w-4 h-4" />}
                {member ? 'Save Changes' : 'Add Member'}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Member Card ───────────────────────────────────────────────────────────────
function MemberCard({ member, onEdit, onDelete }) {
  const relColor = RELATION_COLORS[member.relationship] || 'bg-gray-100 text-gray-700 border-gray-200';
  const initials = (member.full_name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <motion.div layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-[28px] border border-brand-dark p-5 hover:border-accent-gold/40 hover:shadow-md transition-all group">
      <div className="flex items-start gap-4">
        <div className="relative flex-shrink-0">
          {member.avatar_url ? (
            <img src={member.avatar_url} alt={member.full_name}
              className="w-14 h-14 rounded-2xl object-cover border-2 border-brand-light" />
          ) : (
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-navy to-accent-navy/60 flex items-center justify-center border-2 border-brand-light">
              <span className="text-white font-black text-base">{initials}</span>
            </div>
          )}
          {member.is_deceased ? (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gray-400 rounded-full border-2 border-white flex items-center justify-center">
              <span className="text-white text-[8px]">✦</span>
            </div>
          ) : (
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-serif text-accent-navy text-base leading-tight truncate">{member.full_name}</h4>
          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${relColor}`}>
            {member.relationship}
          </span>
          {(member.birth_year || member.death_year) && (
            <p className="text-[10px] text-text-muted mt-1 flex items-center gap-1">
              <SafeIcon icon={FiCalendar} className="w-3 h-3" />
              {member.birth_year || '?'}{member.is_deceased && ` – ${member.death_year || '?'}`}
            </p>
          )}
          {member.bio && <p className="text-[11px] text-text-muted mt-1.5 line-clamp-2 font-light">{member.bio}</p>}
          {member.linked_memorial_name && (
            <div className="mt-2 flex items-center gap-1.5 text-[10px] text-accent-gold font-black uppercase tracking-widest">
              <SafeIcon icon={FiLink} className="w-3 h-3" /> {member.linked_memorial_name}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onEdit(member)} className="p-2 hover:bg-accent-navy/10 rounded-xl transition-colors text-accent-navy">
            <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(member)} className="p-2 hover:bg-red-50 rounded-xl transition-colors text-red-400">
            <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ── Main Tab ──────────────────────────────────────────────────────────────────
export default function FamilyNetworkTab() {
  const [activeTab,    setActiveTab]    = useState('members');
  const [members,      setMembers]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [search,       setSearch]       = useState('');
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saveError,    setSaveError]    = useState('');
  const [notifToast,   setNotifToast]   = useState('');
  const isMounted = useRef(true);

  useFamilyNetworkNotifications();
  useEffect(() => () => { isMounted.current = false; }, []);

  const load = useCallback(async () => {
    const user = getSession();
    if (!user) return;
    setLoading(true); setError('');
    try {
      const res  = await writeFetch(`read/${TABLE}?owner_id=${user.id}&sort=created_at&order=desc&limit=100`);
      const data = await res.json();
      if (!isMounted.current) return;
      if (data.status === 'success') setMembers(Array.isArray(data.data) ? data.data : []);
      else throw new Error('Failed to load family members');
    } catch (e) {
      if (isMounted.current) setError(e.message || 'Failed to load');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg) => {
    setNotifToast(msg);
    setTimeout(() => { if (isMounted.current) setNotifToast(''); }, 4000);
  };

  const handleSave = async (form) => {
    const user = getSession();
    if (!user) return;
    setSaving(true); setSaveError('');
    const prevLinkedId  = editing?.linked_memorial_id || '';
    const prevBirthYear = editing?.birth_year || '';
    const prevDeathYear = editing?.death_year || '';
    const prevBio       = editing?.bio        || '';

    try {
      if (editing) {
        await writeFetch(`update/${TABLE}/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            ...form,
            is_deceased: form.is_deceased ? 1 : 0,
            birth_year: form.birth_year ? parseInt(form.birth_year) : null,
            death_year: form.death_year ? parseInt(form.death_year) : null,
          }),
        });
        const lifeChanged = form.birth_year !== String(prevBirthYear) ||
                            form.death_year !== String(prevDeathYear) ||
                            form.bio !== prevBio;
        if (lifeChanged) {
          const detail = form.is_deceased
            ? `${form.full_name}: ${form.birth_year || '?'} – ${form.death_year || '?'}`
            : `Bio or birth year updated for ${form.full_name}`;
          await notifyLifeEventUpdated({ ownerUserId: user.id, memberName: form.full_name,
            lifeEventType: form.is_deceased ? 'passing_recorded' : 'bio_updated', detail });
          showToast(`Life event recorded for ${form.full_name}`);
        }
        if (form.linked_memorial_id && form.linked_memorial_id !== prevLinkedId) {
          await notifyMemorialTreeLinked({ ownerUserId: user.id, memberName: form.full_name,
            memorialName: form.linked_memorial_name || form.linked_memorial_id });
          showToast(`${form.full_name} linked to ${form.linked_memorial_name}`);
        }
      } else {
        await writeFetch(`create/${TABLE}`, {
          method: 'POST',
          body: JSON.stringify({
            id: crypto.randomUUID(),
            owner_id: user.id,
            ...form,
            is_deceased: form.is_deceased ? 1 : 0,
            birth_year: form.birth_year ? parseInt(form.birth_year) : null,
            death_year: form.death_year ? parseInt(form.death_year) : null,
          }),
        });
        await notifyFamilyMemberAdded({ ownerUserId: user.id, memberName: form.full_name,
          relationship: form.relationship, memorialName: form.linked_memorial_name || null });
        showToast(`${form.full_name} added to your family network`);
        if (form.linked_memorial_id) {
          await notifyMemorialTreeLinked({ ownerUserId: user.id, memberName: form.full_name,
            memorialName: form.linked_memorial_name || form.linked_memorial_id });
        }
      }
      if (!isMounted.current) return;
      setModalOpen(false); setEditing(null);
      await load();
    } catch (e) {
      if (isMounted.current) setSaveError(e.message || 'Save failed');
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const handleDelete = async (member) => {
    try {
      await writeFetch(`delete/${TABLE}/${member.id}`, { method: 'DELETE' });
      if (isMounted.current) {
        setDeleteTarget(null);
        setMembers(prev => prev.filter(m => m.id !== member.id));
      }
    } catch { /* silent */ }
  };

  const filtered = members.filter(m => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (m.full_name || '').toLowerCase().includes(q) ||
           (m.relationship || '').toLowerCase().includes(q) ||
           (m.linked_memorial_name || '').toLowerCase().includes(q);
  });

  const stats = {
    total:    members.length,
    living:   members.filter(m => !m.is_deceased).length,
    deceased: members.filter(m =>  m.is_deceased).length,
    linked:   members.filter(m =>  m.linked_memorial_id).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-[40px] p-8 border border-brand-dark shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-accent-navy to-[#1a2a5e] rounded-[20px] flex items-center justify-center shadow-lg">
              <SafeIcon icon={FiUsers} className="w-7 h-7 text-accent-gold" />
            </div>
            <div>
              <h2 className="text-2xl font-serif text-accent-navy">Family Network</h2>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Your personal family connections</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load}
              className="p-2.5 border-2 border-brand-dark text-text-muted hover:text-accent-navy hover:border-accent-navy rounded-xl transition-all">
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={() => { setEditing(null); setModalOpen(true); setSaveError(''); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-accent-gold text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold/90 transition-all shadow-md shadow-accent-gold/20">
              <SafeIcon icon={FiPlus} className="w-4 h-4" /> Add Member
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total',  value: stats.total,    icon: FiUsers,  color: 'text-accent-navy' },
            { label: 'Living', value: stats.living,   icon: FiHeart,  color: 'text-green-600'   },
            { label: 'Passed', value: stats.deceased, icon: FiUser,   color: 'text-gray-500'    },
            { label: 'Linked', value: stats.linked,   icon: FiLink,   color: 'text-accent-gold' },
          ].map(s => (
            <div key={s.label} className="bg-brand-light rounded-2xl p-4 border border-brand-dark">
              <div className={`flex items-center gap-2 mb-1 ${s.color}`}>
                <SafeIcon icon={s.icon} className="w-4 h-4" />
                <span className="text-[9px] font-black uppercase tracking-widest text-text-muted">{s.label}</span>
              </div>
              <p className={`text-2xl font-serif ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 p-1 bg-brand-light rounded-2xl border border-brand-dark">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                activeTab === tab.id
                  ? 'bg-white shadow-sm text-accent-navy border border-brand-dark'
                  : 'text-text-muted hover:text-accent-navy'
              }`}>
              <SafeIcon icon={tab.icon} className="w-3.5 h-3.5" />
              {tab.label}
              {tab.id === 'suggestions' && members.length > 0 && (
                <span className="w-4 h-4 bg-violet-500 text-white text-[8px] font-black rounded-full flex items-center justify-center">
                  ✦
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab: Members */}
      {activeTab === 'members' && (
        <>
          {/* Info Banner */}
          <div className="flex items-start gap-3 p-4 bg-teal-50 border border-teal-100 rounded-2xl">
            <SafeIcon icon={FiBell} className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-teal-800 font-light">
              <strong>Smart notifications enabled.</strong> You'll receive alerts when family members are added, life events are recorded, or connections are made to memorial trees.
            </p>
          </div>

          {/* Search */}
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, relationship, or memorial…"
              className="w-full pl-11 pr-4 py-3 bg-white border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm" />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-700">
              <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={load} className="text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
                <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" /> Retry
              </button>
            </div>
          )}

          {/* Members Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-white rounded-[28px] border-2 border-brand-dark animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-[40px] p-16 border border-brand-dark text-center">
              <div className="w-20 h-20 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiUsers} className="w-10 h-10 text-accent-gold/40" />
              </div>
              <h3 className="text-2xl font-serif text-accent-navy mb-2">
                {search ? 'No members found' : 'No Family Members Yet'}
              </h3>
              <p className="text-text-muted font-light mb-8 max-w-sm mx-auto">
                {search ? 'Try a different search term.' : "Start building your family network. You'll be notified of all changes automatically."}
              </p>
              {!search && (
                <button onClick={() => { setEditing(null); setModalOpen(true); setSaveError(''); }}
                  className="btn-primary py-4 px-10 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 mx-auto">
                  <SafeIcon icon={FiPlus} className="w-4 h-4" /> Add First Member
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AnimatePresence mode="popLayout">
                {filtered.map(m => (
                  <MemberCard key={m.id} member={m}
                    onEdit={mem => { setEditing(mem); setModalOpen(true); setSaveError(''); }}
                    onDelete={setDeleteTarget} />
                ))}
              </AnimatePresence>
            </div>
          )}
        </>
      )}

      {/* Tab: AI Suggestions */}
      {activeTab === 'suggestions' && (
        <AISuggestionsPanel userMembers={members} />
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {modalOpen && (
          <MemberModal member={editing}
            onClose={() => { setModalOpen(false); setEditing(null); }}
            onSave={handleSave} saving={saving} />
        )}
      </AnimatePresence>

      {/* Delete Confirm */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="bg-white rounded-[28px] p-8 max-w-sm w-full shadow-2xl text-center"
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}>
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiTrash2} className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-serif text-accent-navy mb-2">Remove Member?</h3>
              <p className="text-text-muted font-light text-sm mb-6">
                <strong>{deleteTarget.full_name}</strong> will be permanently removed from your family network.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteTarget(null)}
                  className="flex-1 py-3 border-2 border-brand-dark rounded-2xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-navy transition-colors">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteTarget)}
                  className="flex-1 py-3 bg-red-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-red-600 transition-colors">
                  Remove
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save Error Toast */}
      <AnimatePresence>
        {saveError && (
          <motion.div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-6 py-4 bg-red-600 text-white rounded-2xl shadow-xl"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}>
            <SafeIcon icon={FiAlertTriangle} className="w-5 h-5" />
            <span className="text-sm font-medium">{saveError}</span>
            <button onClick={() => setSaveError('')} className="ml-2 hover:opacity-70">
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Notification Toast */}
      <AnimatePresence>
        {notifToast && (
          <motion.div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-6 py-4 bg-teal-600 text-white rounded-2xl shadow-xl max-w-sm"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}>
            <SafeIcon icon={FiBell} className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">{notifToast}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}